import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import {
  listarGrupos, listarVehiculosSimple, listarUsuariosSimple, editarGrupo, crearGrupo,
} from "../services/trsRastreoService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.78rem",
  padding: "6px 8px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const cellSx = { py: 0.6, px: 1, fontSize: "0.8rem" };

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
};

const getErrorMessage = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo cargar la informacion";

const CountBadge = ({ value, icon, color = "info" }) => (
  value > 0 ? (
    <Chip
      icon={icon}
      label={value}
      size="small"
      color={color}
      variant="outlined"
      sx={{ height: 22, fontSize: "0.72rem", fontWeight: 700, "& .MuiChip-icon": { fontSize: 14, ml: "6px" } }}
    />
  ) : (
    <Typography variant="caption" color="text.disabled">—</Typography>
  )
);

function CheckTable({ title, rows, selectedIds, onToggle, getId, getPrimary, getSecondary, secondaryLabel = "Dato", getSortKey = null, readOnly = false, loading = false }) {
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  // Ordena los seleccionados primero, pero solo una vez (al abrir/cargar), asi la
  // lista no salta de lugar cada vez que el usuario tilda/destilda algo.
  const ordenInicialRef = useRef(null);
  if (ordenInicialRef.current === null && rows.length > 0) {
    ordenInicialRef.current = new Set(selectedIds.map(String));
  }

  const rowsOrdenadas = useMemo(() => {
    const prioridad = ordenInicialRef.current;
    if ((!prioridad || !prioridad.size) && !getSortKey) return rows;
    return [...rows].sort((a, b) => {
      if (prioridad && prioridad.size) {
        const aSel = prioridad.has(String(getId(a))) ? 0 : 1;
        const bSel = prioridad.has(String(getId(b))) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
      }
      if (getSortKey) {
        const aKey = getSortKey(a) ?? "";
        const bKey = getSortKey(b) ?? "";
        if (aKey !== bKey) return aKey > bKey ? 1 : -1;
      }
      return 0;
    });
  }, [rows, getId, getSortKey]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rowsOrdenadas;
    return rowsOrdenadas.filter((row) =>
      [getPrimary(row), getSecondary(row), getId(row)]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [filter, rowsOrdenadas, getId, getPrimary, getSecondary]);

  // Al cambiar el filtro (o los datos) el resultado cambia de tamano, asi que
  // volvemos a la primera pagina para no quedar en una pagina vacia.
  useEffect(() => { setPage(0); }, [filter, rows]);

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const visibleIds = filtered.map(getId).filter((id) => id !== undefined && id !== null);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(String(id)));

  const toggleVisible = () => {
    if (readOnly) return;
    const next = new Set(selectedSet);
    if (allVisibleSelected) visibleIds.forEach((id) => next.delete(String(id)));
    else visibleIds.forEach((id) => next.add(String(id)));
    onToggle([...next]);
  };

  const toggleOne = (id) => {
    if (readOnly) return;
    const next = new Set(selectedSet);
    const key = String(id);
    if (next.has(key)) next.delete(key); else next.add(key);
    onToggle([...next]);
  };

  return (
    <Paper variant="outlined" sx={{ minHeight: 520, overflow: "hidden" }}>
      <Box sx={{ p: 0.75, display: "flex", alignItems: "center", gap: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 900, flex: 1 }}>{title}</Typography>
        <Chip size="small" label={`${selectedSet.size}/${rows.length}`} sx={{ height: 20, fontSize: "0.68rem" }} />
      </Box>
      <Box sx={{ p: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filtrar..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ "& .MuiInputBase-input": { py: 0.55, fontSize: "0.76rem" } }}
        />
      </Box>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.6, fontSize: "0.72rem" } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allVisibleSelected} onChange={toggleVisible} disabled={readOnly} />
              </TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{secondaryLabel}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}><CircularProgress size={18} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>Sin registros.</TableCell></TableRow>
            ) : pageRows.map((row) => {
              const id = getId(row);
              const checked = readOnly || selectedSet.has(String(id));
              return (
                <TableRow key={String(id)} hover={!readOnly} onClick={() => toggleOne(id)} sx={{ cursor: readOnly ? "default" : "pointer" }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={checked} disabled={readOnly} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: checked ? 900 : 600 }}>{getPrimary(row)}</TableCell>
                  <TableCell>{getSecondary(row) || "-"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Filas"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
        sx={{
          "& .MuiToolbar-root": { minHeight: 36, pl: 0.5, pr: 0 },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.68rem" },
          "& .MuiTablePagination-select": { fontSize: "0.68rem" },
        }}
      />
    </Paper>
  );
}

export default function TrsRastreoGruposPage() {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buscar, setBuscar] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const [editGrupo, setEditGrupo] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editVehiculoIds, setEditVehiculoIds] = useState([]);
  const [vehiculoIdsOriginales, setVehiculoIdsOriginales] = useState([]);
  const [todosVehiculos, setTodosVehiculos] = useState([]);
  const [vehiculosLoading, setVehiculosLoading] = useState(false);
  const [editUsuarioIds, setEditUsuarioIds] = useState([]);
  const [usuarioIdsOriginales, setUsuarioIdsOriginales] = useState([]);
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [guardandoGrupo, setGuardandoGrupo] = useState(false);
  const [errorGrupo, setErrorGrupo] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createNombre, setCreateNombre] = useState("");
  const [createActivo, setCreateActivo] = useState(true);
  const [createUsuarioIds, setCreateUsuarioIds] = useState([]);
  const [createVehiculoIds, setCreateVehiculoIds] = useState([]);
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState(null);

  const cargarVehiculos = useCallback(async () => {
    if (todosVehiculos.length) return;
    setVehiculosLoading(true);
    try {
      const res = await listarVehiculosSimple();
      const data = res?.data?.data ?? res?.data;
      setTodosVehiculos(Array.isArray(data) ? data : []);
    } catch (e) {
      setErrorGrupo(getErrorMessage(e));
    } finally {
      setVehiculosLoading(false);
    }
  }, [todosVehiculos.length]);

  const cargarUsuariosSimple = useCallback(async () => {
    if (todosUsuarios.length) return;
    setUsuariosLoading(true);
    try {
      const res = await listarUsuariosSimple();
      const data = res?.data?.data ?? res?.data;
      setTodosUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      setErrorGrupo(getErrorMessage(e));
    } finally {
      setUsuariosLoading(false);
    }
  }, [todosUsuarios.length]);

  const abrirEditar = async (grupo) => {
    setEditGrupo(grupo);
    setEditNombre(grupo.cua_descripcion || "");
    setErrorGrupo(null);
    const idsVehiculoActuales = (grupo.cuadrilla_relacion || [])
      .filter((r) => r.table_rel === "vehiculos" && r.cuarel_activo)
      .map((r) => String(r.for_id));
    setEditVehiculoIds(idsVehiculoActuales);
    setVehiculoIdsOriginales(idsVehiculoActuales);
    const idsUsuarioActuales = (grupo.usuarios_vinculados || []).map((u) => String(u.usuario_id));
    setEditUsuarioIds(idsUsuarioActuales);
    setUsuarioIdsOriginales(idsUsuarioActuales);
    await Promise.all([cargarVehiculos(), cargarUsuariosSimple()]);
  };

  const guardarEdicionGrupo = async () => {
    if (!editGrupo) return;
    setGuardandoGrupo(true);
    setErrorGrupo(null);
    try {
      const originalSet = new Set(vehiculoIdsOriginales);
      const actualSet = new Set(editVehiculoIds);
      const vehiculos_agregar = [...actualSet].filter((id) => !originalSet.has(id)).map(Number);
      const vehiculos_remover = [...originalSet].filter((id) => !actualSet.has(id)).map(Number);

      const usuarioOriginalSet = new Set(usuarioIdsOriginales);
      const usuarioActualSet = new Set(editUsuarioIds);
      const usuarios_agregar = [...usuarioActualSet].filter((id) => !usuarioOriginalSet.has(id)).map(Number);
      const usuarios_remover = [...usuarioOriginalSet].filter((id) => !usuarioActualSet.has(id)).map(Number);

      const nombreCambio = editNombre.trim() && editNombre.trim() !== (editGrupo.cua_descripcion || "");

      await editarGrupo(editGrupo.id, {
        ...(nombreCambio ? { cua_descripcion: editNombre.trim() } : {}),
        vehiculos_agregar,
        vehiculos_remover,
        usuarios_agregar,
        usuarios_remover,
      });

      setEditGrupo(null);
      await cargar();
    } catch (e) {
      setErrorGrupo(getErrorMessage(e));
    } finally {
      setGuardandoGrupo(false);
    }
  };

  const abrirCrear = async () => {
    setCreateNombre("");
    setCreateActivo(true);
    setCreateUsuarioIds([]);
    setCreateVehiculoIds([]);
    setErrorCrear(null);
    setCreateOpen(true);
    await Promise.all([cargarVehiculos(), cargarUsuariosSimple()]);
  };

  const guardarCreacionGrupo = async () => {
    const nombre = createNombre.trim();
    if (!nombre) {
      setErrorCrear("El nombre del grupo es obligatorio.");
      return;
    }
    setCreando(true);
    setErrorCrear(null);
    try {
      await crearGrupo({
        cua_descripcion: nombre,
        cua_activo: createActivo,
        usuarios_web: createUsuarioIds.map(Number),
        vehiculos: createVehiculoIds.map(Number),
      });
      setCreateOpen(false);
      await cargar();
    } catch (e) {
      setErrorCrear(getErrorMessage(e));
    } finally {
      setCreando(false);
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listarGrupos();
      const data = res?.data?.data ?? res?.data;
      setGrupos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const gruposFiltrados = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter((g) => String(g.cua_descripcion || "").toLowerCase().includes(q));
  }, [grupos, buscar]);

  return (
    <DashboardLayout>
      <PageHeader
        title="TRS Rastreo — Grupos"
        subtitle="Administracion y asignacion de grupos"
        backTo="/trsrastreo"
        actions={(
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={cargar} disabled={loading}>
              Actualizar
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={abrirCrear} disabled={loading}>
              Registrar grupo
            </Button>
          </Stack>
        )}
      />

      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label="Buscar"
            placeholder="Nombre del grupo"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            sx={{ ...compactFieldSx, width: 280 }}
          />
          <Typography variant="caption" color="text.secondary">
            {gruposFiltrados.length} de {grupos.length}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>ID</StyledHeaderCell>
                  <StyledHeaderCell>Nombre</StyledHeaderCell>
                  <StyledHeaderCell align="center">Usuarios</StyledHeaderCell>
                  <StyledHeaderCell align="center">Vehiculos</StyledHeaderCell>
                  <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                  <StyledHeaderCell align="center">Acciones</StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : gruposFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No hay grupos para mostrar.</Typography>
                    </TableCell>
                  </TableRow>
                ) : gruposFiltrados.map((g) => (
                  <TableRow key={g.id} hover sx={g.cua_activo ? {} : { opacity: 0.6 }}>
                    <TableCell sx={cellSx}>{g.id}</TableCell>
                    <TableCell sx={cellSx}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>{g.cua_descripcion}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <CountBadge value={Number(g.total_usuarios || 0)} icon={<PersonIcon />} color="secondary" />
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <CountBadge value={(g.cuadrilla_relacion || []).length} icon={<DirectionsCarIcon />} color="info" />
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Chip
                        label={g.cua_activo ? "Activo" : "Inactivo"}
                        size="small"
                        color={g.cua_activo ? "success" : "default"}
                        sx={{ height: 20, fontSize: "0.68rem" }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => setDetalle(g)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar grupo">
                        <IconButton size="small" onClick={() => abrirEditar(g)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
          {detalle?.cua_descripcion}
          <IconButton size="small" onClick={() => setDetalle(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <DirectionsCarIcon fontSize="small" /> Vehiculos ({(detalle?.cuadrilla_relacion || []).length})
              </Typography>
              {(detalle?.cuadrilla_relacion || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin vehiculos en este grupo</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Placa / Alias</TableCell>
                      <TableCell sx={cellSx}>Modem</TableCell>
                      <TableCell sx={cellSx} align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalle.cuadrilla_relacion.map((rel) => {
                      const veh = rel.info_relacionado || {};
                      return (
                        <TableRow key={rel.id}>
                          <TableCell sx={cellSx}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>{veh.veh_placa}</Typography>
                            <Typography variant="caption" color="text.secondary">{veh.veh_alias}</Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {veh.veh_modid ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => setEquipoDialog({ tipo: "modem", identificador: veh.veh_modid })}
                                sx={{
                                  p: 0, minWidth: 0, fontSize: "0.75rem", fontFamily: "monospace",
                                  fontWeight: 700, textTransform: "none",
                                }}
                              >
                                {veh.veh_modid}
                              </Button>
                            ) : "-"}
                          </TableCell>
                          <TableCell sx={cellSx} align="center">
                            <Chip
                              label={rel.cuarel_activo ? "Activo" : "Inactivo"}
                              size="small"
                              color={rel.cuarel_activo ? "success" : "default"}
                              sx={{ height: 18, fontSize: "0.62rem" }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <PersonIcon fontSize="small" /> Usuarios ({(detalle?.usuarios_vinculados || []).length})
              </Typography>
              {(detalle?.usuarios_vinculados || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary">Sin usuarios en este grupo</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Usuario</TableCell>
                      <TableCell sx={cellSx}>Cliente</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalle.usuarios_vinculados.map((u) => (
                      <TableRow key={u.relacion_id}>
                        <TableCell sx={cellSx}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>{u.usu_nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.usu_username} · {u.usu_email || "sin email"}</Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>{u.usu_cliente ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />

      <Dialog open={Boolean(editGrupo)} onClose={() => !guardandoGrupo && setEditGrupo(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>Editar grupo</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 1, pt: 1.5 }}>
          <TextField
            size="small"
            label="Nombre del grupo"
            value={editNombre}
            onChange={(e) => setEditNombre(e.target.value)}
            disabled={guardandoGrupo}
            autoFocus
          />

          {errorGrupo && <Alert severity="error">{errorGrupo}</Alert>}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            <CheckTable
              key={`usuarios-${editGrupo?.id}`}
              title="Asignar usuarios"
              rows={todosUsuarios}
              selectedIds={editUsuarioIds}
              onToggle={setEditUsuarioIds}
              getId={(u) => u.id}
              getPrimary={(u) => u.usu_username}
              getSecondary={(u) => [u.cliente_razon_social, u.cliente_descripcion].filter(Boolean).join(" — ") || u.usu_cliente}
              secondaryLabel="Cliente"
              getSortKey={(u) => u.usu_cliente}
              loading={usuariosLoading}
            />

            <CheckTable
              key={`vehiculos-${editGrupo?.id}`}
              title="Asignar unidades"
              rows={todosVehiculos}
              selectedIds={editVehiculoIds}
              onToggle={setEditVehiculoIds}
              getId={(v) => v.id}
              getPrimary={(v) => v.veh_placa || v.veh_alias}
              getSecondary={(v) => v.veh_modid}
              loading={vehiculosLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={() => setEditGrupo(null)} disabled={guardandoGrupo}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={guardarEdicionGrupo}
            disabled={guardandoGrupo || usuariosLoading || vehiculosLoading}
            startIcon={guardandoGrupo ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => !creando && setCreateOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>Registrar grupo</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 1, pt: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Nombre del grupo"
              value={createNombre}
              onChange={(e) => setCreateNombre(e.target.value)}
              disabled={creando}
              autoFocus
              sx={{ flex: 1, minWidth: 220 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createActivo}
                  onChange={(e) => setCreateActivo(e.target.checked)}
                  disabled={creando}
                />
              )}
              label="Activo"
            />
          </Box>

          {errorCrear && <Alert severity="error">{errorCrear}</Alert>}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            <CheckTable
              key={`create-usuarios-${createOpen}`}
              title="Asignar usuarios"
              rows={todosUsuarios}
              selectedIds={createUsuarioIds}
              onToggle={setCreateUsuarioIds}
              getId={(u) => u.id}
              getPrimary={(u) => u.usu_username}
              getSecondary={(u) => [u.cliente_razon_social, u.cliente_descripcion].filter(Boolean).join(" — ") || u.usu_cliente}
              secondaryLabel="Cliente"
              getSortKey={(u) => u.usu_cliente}
              loading={usuariosLoading}
            />

            <CheckTable
              key={`create-vehiculos-${createOpen}`}
              title="Asignar unidades"
              rows={todosVehiculos}
              selectedIds={createVehiculoIds}
              onToggle={setCreateVehiculoIds}
              getId={(v) => v.id}
              getPrimary={(v) => v.veh_placa || v.veh_alias}
              getSecondary={(v) => v.veh_modid}
              loading={vehiculosLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={() => setCreateOpen(false)} disabled={creando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={guardarCreacionGrupo}
            disabled={creando || usuariosLoading || vehiculosLoading}
            startIcon={creando ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
